import React from 'react';

const sections = [
  {
    title: '1. Introduction',
    body: [
      'Complin.AI (“the App”, “we”, “us”, or “our”) is a compliance management platform designed to help users capture, store, and manage compliance documentation and photos securely.',
      'This Privacy Policy explains how we collect, use, and protect personal data when you use our mobile app or related services.',
      'By using Complin.AI, you agree to this Privacy Policy.'
    ]
  },
  {
    title: '2. Information We Collect',
    body: [
      'We collect only the information necessary to provide and improve our services:',
      'Account Information: Name and email address (used for login, notifications, and user identification) and authentication method (email/password or Google SSO credentials).',
      'Uploaded Content: Photos, forms, and documents you upload as evidence or compliance records. These files may include metadata such as timestamps or embedded location data.',
      'Device & Technical Data: Device type, operating system version, and app usage events (for debugging and performance optimization).'
    ]
  },
  {
    title: '3. How We Use Your Information',
    body: [
      'Your data is used solely to operate and improve the App:',
      '• To authenticate users and manage access.',
      '• To store and display compliance records and photos.',
      '• To process images via Google Vision API for AI-powered text or object extraction.',
      '• To send relevant notifications or reminders related to compliance tasks.',
      '• To provide customer support and technical assistance.',
      'We do not sell, rent, or share your personal information with third parties for marketing purposes.'
    ]
  },
  {
    title: '4. AI and Image Processing',
    body: [
      'When users upload photos or documents, they may be processed by Google Vision API to extract text or structured information.',
      'All processing is automated and performed securely, and data transmitted to and from Google APIs follows Google’s privacy and security policies.'
    ]
  },
  {
    title: '5. Data Storage & Security',
    body: [
      'All user data, including photos and documents, is securely stored on Google Firebase servers located in the United States.',
      'We use encryption in transit (HTTPS/TLS) and role-based access controls to prevent unauthorized access.'
    ]
  },
  {
    title: '6. Data Retention',
    body: [
      'We retain your data as long as your account remains active.',
      'When you delete your account or request data deletion, all associated records (photos, forms, and documents) are permanently removed from Firebase within 30 days.'
    ]
  },
  {
    title: '7. Your Rights',
    body: [
      'Depending on your location, you may have the right to access, correct, or delete your data; withdraw consent to processing; or request data export.',
      'To exercise these rights, contact us at privacy@complin.ai.'
    ]
  },
  {
    title: '8. Third-Party Services',
    body: [
      'We use third-party services to operate Complin.AI, including Google Firebase (user authentication, cloud storage, analytics) and Google Vision API (AI-based document and image analysis).',
      'Each provider maintains its own privacy policy and complies with applicable data protection standards.'
    ]
  },
  {
    title: '9. Children’s Privacy',
    body: [
      'Complin.AI is not intended for users under the age of 16. We do not knowingly collect data from children.',
      'If you believe a child has provided us personal data, please contact us to have it deleted.'
    ]
  },
  {
    title: '10. Data Security Measures',
    body: [
      'We employ encrypted network communication (HTTPS), role-based access control, secure password hashing for email logins, and automatic logout/session management.'
    ]
  },
  {
    title: '11. Changes to This Policy',
    body: [
      'We may update this Privacy Policy periodically. Any changes will be posted within the app and on our website with an updated “Last updated” date.'
    ]
  },
  {
    title: '12. Contact Us',
    body: [
      'For privacy inquiries or data-related requests, contact:',
      'SNN Management Inc',
      'Attn: Privacy Department',
      'Salt Lake City, Utah, USA'
    ]
  }
];

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-wider text-gray-500">Privacy Policy</p>
          <h1 className="text-3xl font-semibold mt-2 text-gray-900">Complin.AI Privacy Policy</h1>
          <p className="mt-3 text-sm text-gray-600">Last updated: November 6, 2025</p>
          <p className="mt-1 text-sm text-gray-600">Developer: SNN Management Inc, Salt Lake City, Utah, USA</p>
        </header>

        <section className="space-y-8 text-sm leading-6 text-gray-700">
          {sections.map((section) => (
            <article key={section.title}>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{section.title}</h2>
              {section.body.map((paragraph, idx) => (
                <p key={idx} className="mb-2 whitespace-pre-line">
                  {paragraph}
                </p>
              ))}
            </article>
          ))}
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;

